import React, { useMemo } from 'react';
import { useProducts } from './useProducts';
import { buildCategoryGroupCards, countProductsInGroup, groupShelfImage } from './catalogGroupUtils';
import { formatDisplayTitle } from './formatProductTitle';
import { useSiteSettings } from './SiteSettingsContext';
import { resolveFeaturedProducts } from './featuredProducts';
import CatalogGridSkeleton from './catalog/CatalogGridSkeleton';
import ProductShelfSection from './components/ProductShelfSection';
import ProductShelfCard from './components/ProductShelfCard';
import CollectionShelfCard from './components/CollectionShelfCard';
import { categoryShelfImage } from './catalog/catalogMedia';

/** Featured products — first thing on home after optional exhibition strip. */
export function HomeFeaturedShelf() {
  const { allProducts, loading } = useProducts();
  const { settings: site } = useSiteSettings();
  const home = site.page_copy?.home || {};

  const featuredProducts = useMemo(
    () => resolveFeaturedProducts(allProducts, site.featured_products, 12),
    [allProducts, site.featured_products],
  );

  if (loading && featuredProducts.length === 0) {
    return (
      <section className="product-shelf home-featured-shelf home-featured-shelf--loading">
        <div className="container store-page__container">
          <CatalogGridSkeleton count={4} />
        </div>
      </section>
    );
  }

  if (featuredProducts.length === 0) return null;

  return (
    <ProductShelfSection
      title={home.featured_title || 'Featured'}
      actionHref="/shop"
      actionLabel={home.featured_action || 'Shop all'}
      layout="scroll"
      className="home-featured-shelf"
      ariaLabel="Featured products"
    >
      {featuredProducts.map((p) => (
        <div className="product-shelf__cell" key={`${p.category}-${p.id}`}>
          <ProductShelfCard product={p} categorySlug={p.category} />
        </div>
      ))}
    </ProductShelfSection>
  );
}

/** Collection + style shelves (no featured block). */
export function HomeCatalogShelves() {
  const { categories, products, loading } = useProducts();
  const { settings: site } = useSiteSettings();
  const home = site.page_copy?.home || {};

  const sortedCats = useMemo(
    () =>
      [...categories].sort((a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug)),
    [categories],
  );

  const groupTiles = useMemo(() => {
    const tiles = [];
    for (const c of sortedCats) {
      const list = products[c.slug] || [];
      if (!list.length) continue;
      const groups = buildCategoryGroupCards(c, list);
      for (const g of groups) {
        const n = countProductsInGroup(list, g.key);
        if (n === 0) continue;
        const image = groupShelfImage(g, list, g.key);
        if (!image) continue;
        tiles.push({
          key: `${c.slug}-${g.key}`,
          title: g.label,
          subtitle: `${n} design${n === 1 ? '' : 's'}`,
          image,
          to: `/shop/${c.slug}/${encodeURIComponent(g.key)}`,
        });
      }
    }
    return tiles;
  }, [sortedCats, products]);

  const spotlight = useMemo(() => groupTiles.slice(0, 12), [groupTiles]);

  if (loading && sortedCats.length === 0) {
    return (
      <section className="home-collections-shelf home-collections-shelf--loading">
        <div className="container store-page__container">
          <CatalogGridSkeleton count={4} />
        </div>
      </section>
    );
  }

  const collectionsTitle = home.collections_title || 'Collections';
  const stylesTitle = home.shop_by_style_title || 'Shop by style';

  return (
    <>
      <ProductShelfSection
        title={collectionsTitle}
        actionHref="/shop"
        actionLabel={home.collections_action || 'View all'}
        layout="scroll"
        className="home-collections-shelf"
        ariaLabel="Shop collections"
      >
        {sortedCats.map((c) => {
          const list = products[c.slug] || [];
          const img = categoryShelfImage(c, list);
          const n = list.length;
          const countLabel = n > 0 ? `${n} design${n === 1 ? '' : 's'}` : 'Soon';
          return (
            <div className="product-shelf__cell" key={c.slug}>
              <CollectionShelfCard
                to={`/shop/${c.slug}`}
                name={formatDisplayTitle(c.name || c.slug)}
                image={img}
                countLabel={countLabel}
              />
            </div>
          );
        })}
      </ProductShelfSection>

      {spotlight.length > 0 ? (
        <ProductShelfSection
          title={stylesTitle}
          layout="scroll"
          className="home-styles-shelf"
          ariaLabel="Shop by style"
        >
          {spotlight.map((t) => (
            <div className="product-shelf__cell" key={t.key}>
              <CollectionShelfCard
                to={t.to}
                name={t.title}
                image={t.image}
                countLabel={t.subtitle}
                compact
              />
            </div>
          ))}
        </ProductShelfSection>
      ) : null}
    </>
  );
}

/** @deprecated Use HomeFeaturedShelf + HomeCatalogShelves */
export default function HomeDynamicCatalog() {
  return (
    <>
      <HomeFeaturedShelf />
      <HomeCatalogShelves />
    </>
  );
}
